import { z } from 'zod';
import { email, id, nonEmpty, optionalText, url } from './common';


// ---------------------------------------------------------------------------
// Sequences (Growth)
// ---------------------------------------------------------------------------

/** Email steps send; task steps remind a person. */
export const SEQUENCE_CHANNELS = ['email', 'task'] as const;
export const sequenceChannel = z.enum(SEQUENCE_CHANNELS);
export type SequenceChannel = z.infer<typeof sequenceChannel>;

export const SEQUENCE_STATUSES = ['draft', 'active', 'paused', 'archived'] as const;
export const sequenceStatus = z.enum(SEQUENCE_STATUSES);
export type SequenceStatus = z.infer<typeof sequenceStatus>;

export const ENROLLMENT_STATUSES = ['active', 'completed', 'stopped'] as const;
export const enrollmentStatus = z.enum(ENROLLMENT_STATUSES);
export type EnrollmentStatus = z.infer<typeof enrollmentStatus>;

export const STOP_REASONS = ['replied', 'meeting_booked', 'opted_out', 'manual', 'bounced', 'completed'] as const;
export const stopReason = z.enum(STOP_REASONS);
export type StopReason = z.infer<typeof stopReason>;

export const sequenceStepInput = z.object({
  channel: sequenceChannel,
  /** Wait after the previous step (or enrollment) before this one runs. */
  delayMinutes: z.number().int().min(0).max(60 * 24 * 60).default(0),
  subject: optionalText(255),
  body: z.string().trim().max(10_000).default(''),
});
export type SequenceStepInput = z.infer<typeof sequenceStepInput>;

export const createSequenceInput = z.object({
  name: nonEmpty(120),
  description: optionalText(1000),
  stopOnReply: z.boolean().default(true),
  stopOnBooked: z.boolean().default(true),
  stopOnOptOut: z.literal(true).default(true),
  steps: z.array(sequenceStepInput).min(1).max(20),
});
export type CreateSequenceInput = z.infer<typeof createSequenceInput>;
export const updateSequenceInput = createSequenceInput.partial().extend({ id, status: sequenceStatus.optional() });
export const enrollInput = z.object({ sequenceId: id, contactIds: z.array(id).min(1).max(500) });
export const recordReplyInput = z.object({ contactId: id, channel: z.enum(['email']).default('email') });

// ---------------------------------------------------------------------------
// Integrations (Growth): API keys, webhooks
// ---------------------------------------------------------------------------



export const API_KEY_SCOPES = ['read', 'write', 'admin'] as const;
export const apiKeyScope = z.enum(API_KEY_SCOPES);
export type ApiKeyScope = z.infer<typeof apiKeyScope>;
export const createApiKeyInput = z.object({ name: nonEmpty(120), scopes: z.array(apiKeyScope).min(1).default(['read']) });
export const WEBHOOK_EVENTS = [
  'contact.created',
  'contact.updated',
  'deal.created',
  'deal.stage_changed',
  'sequence.stopped',
  'content.approved',
  'content.published',
  'content.failed',
  'subscriber.confirmed',
  'subscriber.unsubscribed',
  'conversion.recorded',
] as const;
export const webhookEvent = z.enum(WEBHOOK_EVENTS);
export type WebhookEvent = z.infer<typeof webhookEvent>;
export const createWebhookInput = z.object({ url, events: z.array(webhookEvent).min(1), description: optionalText(255) });

// ---------------------------------------------------------------------------
// Billing (standalone) and onboarding
// ---------------------------------------------------------------------------

export const checkoutInput = z.object({ plan: z.enum(['mkt_starter', 'mkt_growth']) });
export const contactUsInput = z.object({ email, message: z.string().max(2000) });
